/**
 * The portal's modal dialog — @restart/ui's unstyled Modal (the one
 * react-bootstrap builds its own on), mounted inside `.pp-root`, where the
 * portal's design tokens live. It moves focus into the dialog, keeps it
 * there, hands it back to whatever opened the dialog once it closes, closes
 * on Escape, and locks the page's scroll underneath.
 *
 * Always shown: a caller closes it by unmounting it. `closable={false}` holds
 * it open (Escape and a backdrop tap do nothing) while a request is in flight.
 * `closeLabel` is the backdrop's accessible name, in the screen's language.
 */
import React, { createContext, useContext } from 'react';
import Modal from '@restart/ui/Modal';

/** A ref to the `.pp-root` element, provided by PortalShell. */
export const PortalRootContext = createContext(null);

export default function PortalModal({
  className,
  label,
  labelledBy,
  onHide,
  closable = true,
  backdropClassName = '',
  closeLabel = 'Bezárás',
  children,
}) {
  const container = useContext(PortalRootContext);

  const renderBackdrop = ({ ref, onClick }) => (
    <button
      type="button"
      ref={ref}
      className={backdropClassName}
      aria-label={closeLabel}
      tabIndex={-1}
      onClick={onClick}
    />
  );

  // Modal hands over its ref (for focus handling), role="dialog",
  // aria-modal and tabIndex={-1}; the portal adds its own class and name.
  const renderDialog = ({
    ref, role, tabIndex, 'aria-modal': ariaModal,
  }) => (
    <div
      ref={ref}
      role={role}
      tabIndex={tabIndex}
      aria-modal={ariaModal}
      aria-label={label}
      aria-labelledby={labelledBy}
      className={className}
    >
      {children}
    </div>
  );

  return (
    <Modal
      show
      container={container}
      onHide={onHide}
      keyboard={closable}
      backdrop={backdropClassName ? (closable || 'static') : false}
      renderBackdrop={renderBackdrop}
      renderDialog={renderDialog}
    />
  );
}
